import type {
  ChangeEvent,
  ChangeEventHandler,
  FocusEventHandler,
  ForwardedRef,
  KeyboardEventHandler,
  MouseEventHandler,
  WheelEventHandler,
} from 'react';
import { createRef, forwardRef, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import styles from './Input.module.css';

import { useElementState } from '../../hooks/useElementState';
import { useForwardedRef } from '../../hooks/useForwardedRef';
import { FieldLabel } from '../FieldLabel';
import { RevealButton } from './RevealButton';
import type { SpinButtonChangeHandler } from './SpinButtons';
import { SpinButtons } from './SpinButtons';
import { SubmitButton } from './SubmitButton';
import type { InputProps } from './types';
import { isMaskedInput } from './types';
import { useInputMask } from './useInputMask/useInputMask';

/**
 * Render an input field for text or numeric values.
 *
 * @param props - The {@link InputProps} to render the input field.
 * @see https://corgi-x.tfd.engineering/components/input
 */
export const Input = forwardRef(Input_);

function Input_(
  props: InputProps,
  forwardedRef?: ForwardedRef<HTMLInputElement>
) {
  const {
    label = '',
    className,
    invalid,
    highlighted,
    hideLabel = false,
    withSpinButtons = props.type === 'number',
    withRevealButton = false,
    submitButton,
    type,
    ...inputProps
  } = props;
  /** Reference to the input html element. */
  const ref = useForwardedRef(forwardedRef ?? createRef<HTMLInputElement>());
  // Keep the value in the internal state in sync with the value prop.
  const initialValue = props.value !== undefined ? String(props.value) : '';
  const [value, setValue] = useState(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, props.value]);

  const maskProps = useInputMask(ref, props);
  // To toggle password visibility (type="password" vs type="text"),
  // we need to keep track of `type` separately from usage in original props.
  const [pwType, setPwType] = useState(type);

  // autoFocus and disabled should be passed on to native element.
  const [state, handlers] = useElementState({
    autoFocus: props.autoFocus,
    disabled: props.disabled,
    invalid,
    highlighted,
  });

  const handleFocus: FocusEventHandler<HTMLInputElement> = e => {
    e.persist();
    handlers.onFocus(e);
    props.onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = e => {
    e.persist();
    handlers.onBlur(e);
    maskProps.onBlur(e);
    props.onBlur?.(e);
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = e => {
    e.persist();
    setValue(e.target.value);
    maskProps.onChange(e);
    props.onChange?.(e);
  };

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = e => {
    e.persist();
    maskProps.onKeyDown(e);
    props.onKeyDown?.(e);

    if (
      !e.defaultPrevented &&
      e.key === 'Enter' &&
      submitButton &&
      !submitButton.disabled &&
      !inputProps.disabled &&
      !invalid &&
      !ref.current?.form
    ) {
      e.preventDefault();
      submitButtonRef.current?.click();
    }
  };

  const handleStepperChange: SpinButtonChangeHandler = (newValue: number) => {
    // Get current input element.
    const inputElement = ref.current;

    // Create a new Event which carries along all the properties of a native input event.
    const nativeInputEvent = new Event('input', { bubbles: false });

    if (inputElement) {
      // Directly apply new value to input element.
      inputElement.value = String(newValue);

      // Construct a synthetic ChangeEvent based on inputElement.
      const changeEvent = {
        ...nativeInputEvent,
        target: inputElement,
        currentTarget: inputElement,
      } as unknown as ChangeEvent<HTMLInputElement>;

      maskProps.onChange(changeEvent);
      props.onChange?.(changeEvent);
    }
  };

  const handleRevealButtonClick: MouseEventHandler<HTMLButtonElement> = e => {
    e.persist();

    if (pwType === 'password') {
      setPwType('text');
    } else {
      setPwType('password');
    }

    props.onRevealButtonClick?.(e);
  };

  // Prevent user from changing number values with mouse wheel.
  // https://app.shortcut.com/farmersdog/story/123353/corgi-x-number-input-prevent-scroll-from-changing-value
  const handleWheel: WheelEventHandler<HTMLInputElement> = () => {
    if (props.type === 'number') {
      ref.current?.blur();
    }
  };

  const fieldLabelIn =
    props.type === 'date' ||
    state === 'focus' ||
    state === 'invalid' ||
    value?.trim() !== '' ||
    (isMaskedInput(props) && Boolean(maskProps.value));

  const inputType = type === 'password' ? pwType : type;

  useEffect(() => {
    if (props.type !== 'password') {
      return;
    }
    if (!withRevealButton) {
      setPwType('password');
    }
  }, [props, type, withRevealButton]);

  // Placeholder must be unset if the label is displayed in the field (e.g.
  // before focusing), otherwise they will visually overlap each others.
  // We use `aria-placeholder` to keep it unchanged in the accessibility tree.
  const placeholder = !fieldLabelIn ? undefined : inputProps.placeholder;

  return (
    <FieldLabel
      in={fieldLabelIn}
      label={label}
      state={state}
      className={classNames(className, styles.root)}
      hideLabel={hideLabel}
    >
      <input
        {...inputProps}
        ref={ref}
        type={inputType}
        value={isMaskedInput(props) ? maskProps.value : value}
        placeholder={placeholder}
        aria-placeholder={inputProps.placeholder}
        aria-invalid={invalid}
        className={styles.input}
        data-has-submit-button={submitButton ? '' : undefined}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onWheel={handleWheel}
      />
      {withSpinButtons && (
        <SpinButtons
          disabled={inputProps.disabled}
          label={label}
          value={inputProps.value}
          max={inputProps.max}
          min={inputProps.min}
          step={inputProps.step}
          onChange={handleStepperChange}
        />
      )}
      {withRevealButton && props.type === 'password' && (
        <RevealButton
          show={pwType === 'text'}
          onClick={handleRevealButtonClick}
        />
      )}
      {submitButton && (
        <SubmitButton
          {...submitButton}
          ref={submitButtonRef}
          className={styles.submitButton}
          disabled={Boolean(
            submitButton.disabled || inputProps.disabled || invalid
          )}
        />
      )}
    </FieldLabel>
  );
}
